import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  TradeListParams,
  LocalTradeFilters,
  TradeStatus,
  TradeType,
  CollateralType,
  FrameworkAgreement,
  TermType,
} from '@/types';

interface FilterMetadata {
  statuses: TradeStatus[];
  counterparties: string[];
  tradeTypes: TradeType[];
  collateralTypes: CollateralType[];
  agreementTypes: FrameworkAgreement[];
  termTypes: TermType[];
  legalEntities: string[];
  accountIds: string[];
  currencies: string[];
  settlementCurrencies: string[];
  quantityRange: { min: number; max: number };
  contractValueRange: { min: number; max: number };
  marginRange: { min: number; max: number };
  rateRange: { min: number; max: number };
}

interface TradesFilterStore {
  tradesFilters: TradeListParams;
  localTradesFilters: LocalTradeFilters;
  filterMetadata: FilterMetadata;
  setTradesFilters: (filters: TradeListParams) => void;
  setLocalTradesFilters: (filters: LocalTradeFilters) => void;
  setFilterMetadata: (metadata: FilterMetadata) => void;
  clearTradesFilters: () => void;
  clearLocalTradesFilters: () => void;
  resetLocalFilter: (filterId: string) => void;
}

const TRADES_FILTERS_STORAGE_KEY = 'trades-filters-storage';

export const defaultLocalFilters: LocalTradeFilters = {
  statuses: [],
  counterparties: [],
  symbols: [],
  tradeTypes: [],
  collateralTypes: [],
  agreementTypes: [],
  termTypes: [],
  legalEntities: [],
  accountIds: [],
  currencies: [],
  settlementCurrencies: [],
  tradeDateFrom: null,
  tradeDateTo: null,
  settlementDateFrom: null,
  settlementDateTo: null,
  maturityDateFrom: null,
  maturityDateTo: null,
  quantityMin: null,
  quantityMax: null,
  contractValueMin: null,
  contractValueMax: null,
  marginMin: null,
  marginMax: null,
  grossPriceMin: null,
  grossPriceMax: null,
  dailyEarningsMin: null,
  dailyEarningsMax: null,
  createdAtFrom: null,
  createdAtTo: null,
  updatedAtFrom: null,
  updatedAtTo: null,
  rateMin: null,
  rateMax: null,
};

const defaultMetadata: FilterMetadata = {
  statuses: [],
  counterparties: [],
  tradeTypes: [],
  collateralTypes: [],
  agreementTypes: [],
  termTypes: [],
  legalEntities: [],
  accountIds: [],
  currencies: [],
  settlementCurrencies: [],
  quantityRange: { min: 0, max: 1000000 },
  contractValueRange: { min: 0, max: 10000000 },
  marginRange: { min: 1, max: 199 },
  rateRange: { min: 0, max: 100 },
};

/**
 * Full `LocalTradeFilters` from a possibly partial / legacy object: every key
 * of `defaultLocalFilters` is present (fields added after the state was
 * persisted or the view was saved get their default), unknown keys are dropped.
 * Shared by the persist `merge` below and the saved-views adapter (LR-671) —
 * the two must stay in lockstep, like inventory's mergePersistedFilters /
 * withBackfilledFilters pair.
 */
export function backfillLocalTradeFilters(
  partial: Partial<LocalTradeFilters> | undefined
): LocalTradeFilters {
  if (!partial || typeof partial !== 'object') return defaultLocalFilters;
  const out: Record<string, unknown> = { ...defaultLocalFilters };
  for (const key of Object.keys(defaultLocalFilters)) {
    if (key in partial) out[key] = (partial as Record<string, unknown>)[key];
  }
  return out as unknown as LocalTradeFilters;
}

export const useTradesFilterStore = create<TradesFilterStore>()(
  devtools(
    persist(
      (set) => ({
        tradesFilters: {},
        localTradesFilters: defaultLocalFilters,
        filterMetadata: defaultMetadata,
        setTradesFilters: (filters) => set({ tradesFilters: filters }),
        setLocalTradesFilters: (filters) => set({ localTradesFilters: filters }),
        setFilterMetadata: (metadata) => set({ filterMetadata: metadata }),
        clearTradesFilters: () => set({ tradesFilters: {} }),
        clearLocalTradesFilters: () => set({ localTradesFilters: defaultLocalFilters }),
        resetLocalFilter: (filterId: string) => set((state) => {
          const f = { ...state.localTradesFilters };
          switch (filterId) {
            case 'statuses': f.statuses = []; break;
            case 'counterparties': f.counterparties = []; break;
            case 'symbols': f.symbols = []; break;
            case 'tradeTypes': f.tradeTypes = []; break;
            case 'collateralTypes': f.collateralTypes = []; break;
            case 'agreementTypes': f.agreementTypes = []; break;
            case 'termTypes': f.termTypes = []; break;
            case 'legalEntities': f.legalEntities = []; break;
            case 'accountIds': f.accountIds = []; break;
            case 'currencies': f.currencies = []; break;
            case 'settlementCurrencies': f.settlementCurrencies = []; break;
            case 'tradeDate': f.tradeDateFrom = null; f.tradeDateTo = null; break;
            case 'settlementDate': f.settlementDateFrom = null; f.settlementDateTo = null; break;
            case 'maturityDate': f.maturityDateFrom = null; f.maturityDateTo = null; break;
            case 'quantity': f.quantityMin = null; f.quantityMax = null; break;
            case 'contractValue': f.contractValueMin = null; f.contractValueMax = null; break;
            case 'margin': f.marginMin = null; f.marginMax = null; break;
            case 'grossPrice': f.grossPriceMin = null; f.grossPriceMax = null; break;
            case 'dailyEarnings': f.dailyEarningsMin = null; f.dailyEarningsMax = null; break;
            case 'createdAt': f.createdAtFrom = null; f.createdAtTo = null; break;
            case 'updatedAt': f.updatedAtFrom = null; f.updatedAtTo = null; break;
          }
          return { localTradesFilters: f };
        }),
      }),
      {
        name: TRADES_FILTERS_STORAGE_KEY,
        version: 0,
        // LR-671: saved views capture `localTradesFilters`, and the saved-views
        // invariant is "every captured field is persisted by its owning store" —
        // otherwise F5 clears the field in memory while the snapshot still holds
        // it, and the view reads dirty after a reload the user never triggered.
        // Metadata / server params stay in-memory as before.
        partialize: (state) => ({ localTradesFilters: state.localTradesFilters }),
        merge: (persistedState: unknown, currentState: TradesFilterStore): TradesFilterStore => {
          const persisted =
            typeof persistedState === 'object' && persistedState !== null
              ? (persistedState as { localTradesFilters?: Partial<LocalTradeFilters> })
              : undefined;
          return {
            ...currentState,
            localTradesFilters: backfillLocalTradeFilters(persisted?.localTradesFilters),
          };
        },
      }
    ),
    {
      name: 'trades-filter-store', enabled: import.meta.env.DEV,
    }
  )
);

